<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { ColorPicker } from "vue3-colorpicker";
import { useI18n } from "vue-i18n";
import draggable from "vuedraggable";
import cloneDeep from "lodash-es/cloneDeep";
import debounce from "lodash-es/debounce";
import get from "lodash-es/get";
import "vue3-colorpicker/style.css";
// types
import type {
  ReportType,
  ReportBlockType,
  FilterType,
} from "@/common/types/report";
// utils
import { bsonId } from "@/common/utils/common";
import { sanitizeText } from "@/common/utils/text";
import { getDateAndHour, getDiffBreaks, getDate } from "@/common/utils/dates";
// components
import { baseSelect, baseSwitch, svgIcon } from "@/common/components";

const { t, tm } = useI18n();

const props = defineProps<{
  block: ReportBlockType;
  report: ReportType;
  zoom: number;
}>();

const emit = defineEmits(["block:update"]);

const DEFAULT_STYLE = {
  background: "#ffffff",
  color: "#000000",
  "font-size": "16px",
  "font-style": "normal",
  "font-family": "Calibri",
  "font-weight": "normal",
  "line-height": "1.5",
  "text-align": "left",
  "text-decoration": "none",
};

const internalBlock = ref(cloneDeep(props.block));
const internalStyle = ref({ ...DEFAULT_STYLE });
const selectedTab = ref<string>("query");
const columns = ref<any[]>([]);
const fields = ref<any[]>([]);
const rows = ref<any[]>([]);

const filter = ref<FilterType>({
  custom: null,
  field: null,
  table: null,
  value: null,
});

const filterOptions = ref<{ label: string; value: any }[]>([]);

function parseMetadata(raw: any): any {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function toArray(value: any): any[] {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

function looseEqual(a: any, b: any): boolean {
  return (
    String(a ?? "")
      .trim()
      .toLowerCase() ===
    String(b ?? "")
      .trim()
      .toLowerCase()
  );
}

function isEmptyValue(value: any): boolean {
  return (
    value === null || value === undefined || value === "" || value === "custom"
  );
}

/**
 * Resolve o valor de uma célula, tratando imagens.
 * - Se não for imagem: devolve o valor em string.
 * - Se for imagem: devolve uma data URL pronta (a partir de avatar.file ou avatar.base64).
 */
function resolveCellValue(raw: any, isImage: boolean): string {
  if (!isImage) {
    if (raw === null || raw === undefined) return "";
    if (Array.isArray(raw)) {
      return raw.map((r) => resolveCellValue(r, false)).join(", ");
    }
    return String(raw);
  }

  if (!raw) return "";

  // já é data URL
  if (typeof raw === "string") {
    return raw.startsWith("data:") ? raw : "";
  }

  // formato equipamento/forms: { file, mime_type }
  if (raw.file) {
    return `data:${raw.mime_type ?? "image/png"};base64,${raw.file}`;
  }

  // formato empresa: { avatar_id, base64 }
  if (raw.base64) return raw.base64;

  return "";
}

const formatAnalyse = (analyse: any) => {
  if (!analyse) {
    return {
      code: "",
      danger: 0,
      description: "",
      start_edited_motive: "",
      end_edited_motive: "",
      canceled_description: "",
      status: "",
      time: null,
      start_date: "",
      end_date: "",
      canceled_date: "",
    };
  }

  return {
    code: analyse.code,
    danger: analyse.danger,
    description: analyse.description,
    start_edited_motive: analyse.start_edited_motive,
    end_edited_motive: analyse.end_edited_motive,
    canceled_description: analyse.canceled_description,
    status: titles.value[analyse.status?.toLowerCase() as never] ?? "",
    time: !analyse.start_date
      ? null
      : getDiffBreaks(
          analyse.start_date,
          analyse.end_date,
          analyse.breaks || [],
        ).text,
    start_date: getDateAndHour(analyse.start_date),
    end_date: getDateAndHour(analyse.end_date),
    canceled_date: getDateAndHour(analyse.canceled_date),
  };
};

const formatEquipment = (equipment: any) => {
  return {
    ...equipment,
    // ⚠️ Não sobrescrever o avatar — ele já vem com { file, mime_type } do backend
    calibration_date: getDate(equipment.calibration_date),
    expired_date: getDate(equipment.expired_date),
    fabrication_date: getDate(equipment.fabrication_date),
    next_calibration: getDate(equipment.next_calibration),
  };
};

const colOptions = computed(() =>
  columns.value
    .map((col: any) => ({ label: col.label, value: col.id }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label)),
);

const titles = computed(() => ({
  analyse: "Análise",
  canceled: "Cancelado",
  canceled_date: "Data de Cancelamento",
  canceled_description: "Motivo de Cancelamento",
  code: "Código",
  concluded: "Concluído",
  custom: "Personalizado",
  data: "Dados",
  danger: "Grau de Risco",
  description: "Descrição",
  equipment: "Equipamento",
  employee: "Funcionário",
  end_date: "Término",
  end_edited_motive: "Término Editado Motivo",
  evaluation: "Avaliação",
  name: "Nome",
  full_document: "Documento",
  ges_description: "GES Descrição",
  ges_name: "GES Nome",
  ges_status: "GES Status",
  registration_number: "Matricula",
  role: "Cargo",
  sample: "Amostra",
  sector: "Setor",
  social_name: "Nome Social",
  paused: "Pausado",
  pending: "Pendente",
  result: "Resultado",
  started: "Iniciado",
  status: "Status",
  start_date: "Início",
  start_edited_motive: "Início Editado Motivo",
  method: "Método",
}));

const evaluationData = computed(() => {
  if (!props.report?.evaluation) return null;

  return {
    ...props.report?.evaluation,
    start_date: getDateAndHour(props.report?.evaluation?.start_date),
    end_date: getDateAndHour(props.report?.evaluation?.end_date),
    period_start_date: getDateAndHour(
      props.report?.evaluation?.period_start_date,
    ),
    period_end_date: getDateAndHour(props.report?.evaluation?.period_end_date),
  };
});

const companyData = computed(() => {
  if (!props.report?.company) return null;

  const employeeEnum = tm("options.number_of_employees") as Record<
    string,
    string
  >;

  const company = props.report?.company;

  let document = null;
  let number_of_employee = null;

  if (company.document_type) {
    document = `${company.document_name?.toUpperCase()} ${company.document_number}`;
  }

  if (company.number_of_employee) {
    number_of_employee = employeeEnum[company.number_of_employee];
  }

  return {
    cnae: company.cnae,
    danger: company.danger,
    description: company.description,
    document: document,
    fantasy_name: company.fantasy_name,
    name: company.name,
    number_of_employee: number_of_employee,
  };
});

const equipmentData = computed(() => {
  const direct = props.report?.equipments ?? [];

  const fromAnalysis = (props.report?.analysis ?? []).flatMap(
    (a: any) => a?.equipments ?? [],
  );

  const map = new Map<string, any>();

  [...direct, ...fromAnalysis].forEach((eq: any) => {
    if (eq?.equipment_id) {
      map.set(eq.equipment_id, eq);
    }
  });

  return Array.from(map.values()).map((eq) => formatEquipment(eq));
});

// ⬇️ CORREÇÃO: `results` (plural) para bater com `resultMap.field`
const analysisData = computed(() =>
  (props.report?.analysis ?? []).map((analyse: any) => ({
    data: formatAnalyse(analyse),
    evaluation: evaluationData.value,
    employee: analyse?.employee,
    equipment: analyse?.equipments ?? [],
    results: analyse?.results ?? [],
  })),
);

const resultData = computed(() => ({
  analysis: analysisData.value,
  evaluation: {
    data: evaluationData.value,
    company: companyData.value,
  },
  equipment: equipmentData.value.map((equipment: any) => ({
    data: equipment,
  })),
  results: (props.report?.analysis ?? [])
    .flatMap((analyse: any) => analyse?.results ?? [])
    .map((result: any) => {
      const analyse = (props.report?.analysis ?? []).find(
        (a: any) => a.analysis_id === (result.analysis_id ?? result.owner_id),
      );

      if (!analyse) return null;

      const methods = (analyse.methods ?? []).find(
        (m: any) => m.method_id === result.method_id,
      );

      const samples = (analyse.samples ?? []).find(
        (s: any) => s.sample_id === result.sample_id,
      );

      const resultMeta = parseMetadata(result.metadata) ?? {};

      const customResult = [
        ...(resultMeta.fields_ai ?? []),
        ...(resultMeta.fields ?? []),
      ].map((field: any) => ({
        id: field.id,
        label: field.name,
        value: field.answer,
      }));

      const answersMeta = parseMetadata(analyse.answers?.metadata) ?? {};

      const answer = {
        ...(answersMeta.form ?? {}),
        ...(answersMeta.method ?? {}),
      };

      const customAnswer: any[] = [];
      Object.entries(answer).forEach(([key, value]) => {
        const form = (analyse.forms ?? []).find((f: any) => f.form_id === key);
        const method = (analyse.methods ?? []).find(
          (m: any) => m.method_id === key,
        );

        Object.entries(value as any).forEach(([questionKey, questionValue]) => {
          const question =
            form?.questions?.find((q: any) => q.question_id === questionKey) ??
            method?.questions?.find((f: any) => f.question_id === questionKey);

          if (question) {
            customAnswer.push({
              id: question.question_id,
              label: question.name,
              value: questionValue,
            });
          }
        });
      });

      return {
        data: result,
        analyse: formatAnalyse(analyse),
        employee: analyse?.employee,
        equipment: analyse?.equipments ?? [],
        evaluation: props.report?.evaluation,
        form: analyse?.forms ?? [],
        method: methods,
        sample: samples,
        custom_result: customResult,
        custom_answer: customAnswer,
      };
    })
    .filter(Boolean),

  employee: (props.report?.analysis ?? []).map((analyse: any) => ({
    data: analyse?.employee ?? null,
    analyse: formatAnalyse(analyse),
  })),
}));

// ⬇️ Também alinhado para `results` (plural) por consistência
const resultDataB = computed(() => ({
  analysis: (props.report?.analysis ?? []).map((analyse: any) => ({
    data: formatAnalyse(analyse),
    evaluation: props.report?.evaluation,
    employee: analyse?.employee,
    answers: analyse?.answers,
    breaks: analyse?.breaks ?? [],
    forms: analyse?.forms ?? [],
    method: analyse?.methods ?? [],
    results: (analyse?.results ?? []).map((result: any) => {
      const resultMeta = parseMetadata(result.metadata) ?? {};

      const fields = [
        ...(resultMeta.fields_ai ?? []),
        ...(resultMeta.fields ?? []),
      ].map((field: any) => ({
        id: field.id,
        label: field.name,
        value: field.answer,
      }));

      return { ...result, custom: fields };
    }),
    equipment: analyse?.equipments ?? [],
    sample: analyse?.samples ?? [],
    technical: analyse?.technical,
  })),
  equipment: equipmentData.value.map((equipment: any) => ({
    data: equipment,
  })),
  evaluation: {
    data: props.report?.evaluation,
    equipment: equipmentData.value,
  },
  employee: {
    data: (props.report?.analysis ?? []).flatMap((a: any) => a?.employee ?? []),
  },
  methods: (props.report?.analysis ?? [])
    .flatMap((analyse: any) => analyse?.methods ?? [])
    .map((method: any) => {
      const analyse = (props.report?.analysis ?? []).find(
        (a: any) => a.analysis_id === method.analysis_id,
      );

      const custom = method.metadata ?? [];

      return {
        analyse,
        custom,
        data: method,
        employee: analyse?.employee,
        evaluation: props.report?.evaluation,
        sample: analyse?.samples ?? [],
        form: analyse?.forms ?? [],
      };
    }),
  results: (props.report?.analysis ?? [])
    .flatMap((analyse: any) => analyse?.results ?? [])
    .map((result: any) => {
      const analyse = (props.report?.analysis ?? []).find(
        (a: any) => a.analysis_id === (result.analysis_id ?? result.owner_id),
      );

      if (!analyse) return null;

      const resultMeta = parseMetadata(result.metadata) ?? {};
      const custom = resultMeta.fields_ai ?? [];

      const methods = (analyse.methods ?? []).find(
        (m: any) => m.method_id === result.method_id,
      );

      const samples = (analyse.samples ?? []).find(
        (s: any) => s.sample_id === result.sample_id,
      );

      const answersMeta = parseMetadata(analyse.answers?.metadata) ?? {};

      const answer = {
        ...(answersMeta.form ?? {}),
        ...(answersMeta.method ?? {}),
      };

      const answerQuestions: any[] = [];
      Object.entries(answer).forEach(([key, value]) => {
        const form = (analyse.forms ?? []).find((f: any) => f.form_id === key);
        const method = (analyse.methods ?? []).find(
          (m: any) => m.method_id === key,
        );

        Object.entries(value as any).forEach(([questionKey, questionValue]) => {
          const question =
            form?.questions?.find((q: any) => q.question_id === questionKey) ??
            method?.questions?.find((f: any) => f.question_id === questionKey);

          if (question) {
            answerQuestions.push({ ...question, answer: questionValue });
          }
        });
      });

      return {
        analyse,
        answer: answerQuestions,
        custom,
        data: result,
        employee: analyse?.employee,
        equipment: analyse?.equipments ?? [],
        evaluation: props.report?.evaluation,
        form: analyse?.forms ?? [],
        method: methods,
        sample: samples,
      };
    })
    .filter(Boolean),
}));

const analyseMap = computed(() => ({
  field: "analyse",
  label: "Análise",
  list: [
    { field: "code", label: "Código" },
    { field: "danger", label: "Grau de Risco" },
    { field: "description", label: "Descrição" },
    { field: "status", label: "Status" },
    { field: "start_date", label: "Início" },
    { field: "start_edited_motive", label: "Início Editado Motivo" },
    { field: "end_date", label: "Término" },
    { field: "end_edited_motive", label: "Término Editado Motivo" },
    { field: "time", label: "Tempo" },
    { field: "canceled_date", label: "Data de Cancelamento" },
    { field: "canceled_description", label: "Motivo de Cancelamento" },
  ],
}));

const companyMap = computed(() => ({
  field: "company",
  label: "Empresa",
  list: [
    { field: "name", label: "Nome" },
    { field: "fantasy_name", label: "Nome Fantasia" },
    { field: "document", label: "Documento" },
    { field: "cnae", label: "CNAE" },
    { field: "number_of_employee", label: "Número de Funcionários" },
    { field: "danger", label: "Grau de Risco" },
    { field: "description", label: "Descrição" },
  ],
}));

const answerMap = computed(() => ({
  field: "answer",
  label: "Perguntas",
  list: [{ field: "custom_answer", label: "Personalizado" }],
}));

const equipmentMap = computed(() => ({
  field: "equipments",
  label: "Equipamentos",
  list: [
    { field: "avatar", label: "Foto", is_image: true },
    { field: "name", label: "Nome" },
    { field: "code", label: "Código" },
    { field: "brand", label: "Marca" },
    { field: "model", label: "Modelo" },
    { field: "calibration_date", label: "Data de Calibração" },
    { field: "expired_date", label: "Data de Expiração" },
    { field: "fabrication_date", label: "Data de Fabricação" },
    { field: "next_calibration", label: "Próxima Calibração" },
    { field: "serial_number", label: "Número de Série" },
    { field: "description", label: "Descrição" },
  ],
}));

const evaluationMap = computed(() => ({
  field: "evaluation",
  label: "Avaliação",
  isFind: false,
  list: [
    { field: "custom", label: "Personalizado..." },
    { field: "code", label: "Código" },
    { field: "description", label: "Descrição" },
    { field: "status", label: "Status" },
    { field: "start_date", label: "Início" },
    { field: "period_start_date", label: "Previsão de Início" },
    { field: "period_end_date", label: "Previsão de Término" },
    { field: "end_date", label: "Término" },
    { field: "progress", label: "Progresso" },
    { field: "time", label: "Tempo" },
  ],
}));

const employeeMap = computed(() => ({
  field: "employee",
  label: "Funcionário",
  list: [
    { field: "name", label: "Nome" },
    { field: "full_document", label: "Documento" },
    { field: "ges_description", label: "GES Descrição" },
    { field: "ges_name", label: "GES Nome" },
    { field: "ges_status", label: "GES Status" },
    { field: "registration_number", label: "Matricula" },
    { field: "role", label: "Cargo" },
    { field: "sector", label: "Setor" },
    { field: "social_name", label: "Nome Social" },
    { field: "status", label: "Status" },
  ],
}));

const methodGroupMap = computed(() => ({
  field: "method_group",
  label: "Grupo de Métodos",
  list: [
    { field: "name", label: "Nome" },
    { field: "code", label: "Código" },
  ],
}));

const methodMap = computed(() => ({
  field: "method",
  label: "Método",
  list: [
    { field: "name", label: "Nome" },
    { field: "code", label: "Código" },
  ],
}));

const formMap = computed(() => ({
  field: "forms",
  label: "Formulários",
  list: [
    { field: "name", label: "Nome" },
    { field: "code", label: "Código" },
  ],
}));

const resultMap = computed(() => ({
  field: "results",
  label: "Resultado",
  list: [
    { field: "custom_result", label: "Personalizado..." },
    { field: "name", label: "Nome" },
    { field: "result", label: "Resultado" },
    { field: "description", label: "Descrição" },
  ],
}));

const sampleMap = computed(() => ({
  field: "sample",
  label: "Amostras",
  list: [
    { field: "name", label: "Nome" },
    { field: "code", label: "Código" },
  ],
}));

const signatureMap = computed(() => ({
  field: "signatures",
  label: "Assinaturas",
  list: [
    { field: "name", label: "Nome" },
    { field: "code", label: "Código" },
  ],
}));

const crewMap = computed(() => ({
  field: "crew",
  label: "Equipe",
  list: [
    { field: "name", label: "Nome" },
    { field: "type", label: "Tipo" },
  ],
}));

const dataMap = computed(() => [
  {
    table: "analysis",
    label: "Análises",
    list: [
      { ...analyseMap.value, field: "data", label: "Dados da Análise" },
      evaluationMap.value,
      employeeMap.value,
      resultMap.value,
    ],
  },
  {
    table: "evaluation",
    label: "Avaliação",
    list: [
      { ...evaluationMap.value, field: "data", label: "Dados da Avaliação" },
      companyMap.value,
    ],
  },
  {
    table: "equipment",
    label: "Equipamentos",
    list: [
      { ...equipmentMap.value, field: "data", label: "Dados do Equipamento" },
    ],
  },
  {
    table: "results",
    label: "Resultados",
    list: [
      { ...resultMap.value, field: "data", label: "Dados do Resultado" },
      analyseMap.value,
      answerMap.value,
      employeeMap.value,
      equipmentMap.value,
      evaluationMap.value,
      methodMap.value,
      sampleMap.value,
    ],
  },
  {
    table: "employee",
    label: "Funcionários",
    list: [
      { ...employeeMap.value, field: "data", label: "Dados do Funcionário" },
    ],
  },
]);

const optionsFonts = computed(() => [
  { option: "Arial", value: "Arial" },
  { option: "Calibri", value: "Calibri" },
  { option: "Courier", value: "Courier" },
  { option: "Georgia", value: "Georgia" },
  { option: "Open Sans", value: "Open Sans" },
  { option: "Sans Serif", value: "sans-serif" },
  { option: "Times New Roman", value: "Times New Roman" },
  { option: "Verdana", value: "Verdana" },
]);

const optionsFontSize = computed(() => [
  { option: "10px", value: "10px" },
  { option: "11px", value: "11px" },
  { option: "12px", value: "12px" },
  { option: "13px", value: "13px" },
  { option: "14px", value: "14px" },
  { option: "15px", value: "15px" },
  { option: "16px", value: "16px" },
  { option: "17px", value: "17px" },
  { option: "18px", value: "18px" },
  { option: "20px", value: "20px" },
  { option: "22px", value: "22px" },
  { option: "24px", value: "24px" },
  { option: "26px", value: "26px" },
  { option: "28px", value: "28px" },
  { option: "30px", value: "30px" },
]);

const tabs = computed(() => [
  { key: "query", label: "Dados" },
  { key: "cols", label: "Colunas" },
  { key: "fields", label: "Campos" },
  { key: "style", label: "Estilo" },
]);

const disableFilterAdd = computed(
  () =>
    (!filter.value.table && !filter.value.field && !filter.value.value) ||
    (!filter.value.table && !filter.value.field),
);

const disableFilterClear = computed(
  () => !filter.value.table && !filter.value.field && !filter.value.value,
);

function initialize(block: ReportBlockType) {
  internalBlock.value = {
    ...block,
    style: {
      ...DEFAULT_STYLE,
      ...block.style,
    },
  };

  internalStyle.value = {
    ...DEFAULT_STYLE,
    ...block.style,
  };

  columns.value = (internalBlock.value.content.columns || []).filter(
    (item: any) => !!item.visible,
  );

  fields.value = (internalBlock.value.content.fields || []).filter(
    (item: any) => !!item.visible,
  );

  rows.value = (internalBlock.value.content.rows || []).filter(
    (item: any) => !!item.visible,
  );
}

function getTitle(value: any) {
  const title = titles.value[value?.toLowerCase() as never] ?? value;

  if (typeof title === "string") {
    if (String(title).length > 40) {
      return `${String(title).substring(0, 40)}...`;
    }

    return title;
  }

  return title;
}

function onUpdate(key: keyof typeof internalBlock.value, value: any) {
  internalBlock.value = {
    ...internalBlock.value,
    [key]: value,
  };

  emit("block:update", {
    block_id: internalBlock.value.block_id,
    payload: { [key]: value },
  });
}

function changeSwitch(
  key: keyof typeof internalStyle.value,
  checked: boolean,
  value: string,
) {
  const defaultValues = {
    "font-weight": "normal",
    "font-style": "normal",
    "text-align": "left",
  };

  if (!checked) {
    internalStyle.value[key] = defaultValues[key as never];
  } else {
    internalStyle.value[key] = value;
  }

  onUpdate("style", internalStyle.value);
}

function normalizeHex(e: any) {
  let v = e.target.value.toLowerCase();

  if (!v.startsWith("#")) v = "#" + v;

  v = v.replace(/[^#0-9a-f]/g, "");

  if (v.length > 7) v = v.slice(0, 7);

  e.target.value = v;
  internalStyle.value.background = v;
}

function onChangeTable() {
  internalBlock.value.content = {
    ...internalBlock.value.content,
    fields: (internalBlock.value.content.fields ?? []).map((item: any) => ({
      ...item,
      query: {
        ...item.query,
        table: null,
        group: null,
        field: null,
        custom: null,
      },
    })),
    filters: [],
    rows: [],
  };

  onChangeFilter();
}

function onChangeGroup(field: any) {
  if (!field.query.group) {
    field.query.field = null;
    field.query.custom = null;
  }

  onChangeFilter();
}

// -------------------------
// COLS
// -------------------------

function onInputCol() {
  onUpdate("content", internalBlock.value.content);
}

function addCol() {
  const newCol = {
    id: bsonId(),
    label: `Coluna ${internalBlock.value.content.columns.length + 1}`,
    visible: true,
    query: { group: null, field: null, custom: null },
  };

  const payload = {
    ...internalBlock.value.content,
    columns: [...internalBlock.value.content.columns, newCol],
  };

  onUpdate("content", payload);
}

/**
 * Reordenar colunas não precisa mexer nas rows.
 * O renderizador (`table-block`) itera as colunas para montar as células,
 * então a ordem visual é dada pela ordem de `columns`.
 */
function colReorder() {
  onUpdate("content", internalBlock.value.content);
}

function colMoveUp(index: number) {
  const cols = internalBlock.value.content.columns;
  const col = cols[index];

  if (!col || index === 0) return;

  cols.splice(index, 1);
  cols.splice(index - 1, 0, col);

  onUpdate("content", internalBlock.value.content);
}

function colMoveDown(index: number) {
  const cols = internalBlock.value.content.columns;
  const col = cols[index];

  if (!col || index === cols.length - 1) return;

  cols.splice(index, 1);
  cols.splice(index + 1, 0, col);

  onUpdate("content", internalBlock.value.content);
}

/**
 * Remove uma coluna.
 * Suporta os dois formatos de `rows`:
 *  - data-block:  row é array direto  → [ {col, field, value}, ... ]
 *  - table-block: row é { items: [...] }
 */
function colRemove(index: number) {
  const cols = internalBlock.value.content.columns;
  const colToRemove = cols[index];

  if (!colToRemove) return;

  cols.splice(index, 1);

  const payload = {
    ...internalBlock.value.content,
    columns: cols,
    rows: (internalBlock.value.content.rows ?? []).map((row: any) => {
      if (Array.isArray(row)) {
        return row.filter((cell: any) => cell.col !== colToRemove.id);
      }

      if (row?.items) {
        return {
          ...row,
          items: row.items.filter((item: any) => item.col !== colToRemove.id),
        };
      }

      return row;
    }),
  };

  onUpdate("content", payload);
}

// -------------------------
// FIELDS
// -------------------------

function fieldAdd() {
  const newField = {
    col: null,
    id: bsonId(),
    label: "",
    query: { custom: null, field: null, group: null },
    visible: true,
  };

  internalBlock.value.content = {
    ...internalBlock.value.content,
    fields: [...internalBlock.value.content.fields, newField],
  };

  onChangeFilter();
}

function fieldReorder() {
  // reservado
}

function fieldMoveUp(index: number) {
  const fieldList = internalBlock.value.content.fields;
  const field = fieldList[index];

  if (!field) return;

  fieldList.splice(index, 1);
  fieldList.splice(index - 1, 0, field);

  internalBlock.value.content.fields = fieldList;

  onChangeFilter();
}

function fieldMoveDown(index: number) {
  const fieldList = internalBlock.value.content.fields;
  const field = fieldList[index];

  if (!field) return;

  fieldList.splice(index, 1);
  fieldList.splice(index + 1, 0, field);

  internalBlock.value.content.fields = fieldList;

  onChangeFilter();
}

function fieldChangeCol(index: number) {
  const fieldList = internalBlock.value.content.fields;
  const field = fieldList[index];

  if (!field) return;

  fieldList.splice(index, 1);
  fieldList.splice(index + 1, 0, field);

  internalBlock.value.content.fields = fieldList;

  onChangeFilter();
}

function fieldRemove(index: number) {
  const fieldList = internalBlock.value.content.fields;
  const fieldToRemove = fieldList[index];

  if (!fieldToRemove) return;

  fieldList.splice(index, 1);

  internalBlock.value.content.fields = fieldList;

  onChangeFilter();
}

function fieldChangeField(index: number) {
  const fieldList = internalBlock.value.content.fields;
  const field = fieldList[index];

  if (!field) return;

  const table = dataMap.value.find(
    (t) => t.table === internalBlock.value.content.table,
  );

  const group = table?.list?.find(
    (g: any) => g.field === field.query.group,
  ) as any;

  const col = group?.list?.find(
    (f: any) => f.field === field.query.field,
  ) as any;

  field.label = col?.label ?? group?.label ?? "";

  fieldList.splice(index, 1);
  fieldList.splice(index + 1, 0, field);

  internalBlock.value.content.fields = fieldList;

  onChangeFilter();
}

function isTableSelected() {
  return !!internalBlock.value.content.table;
}

function getTableGroups() {
  const tableData = dataMap.value.find(
    (t) => t.table === internalBlock.value.content.table,
  );

  if (!tableData?.list) return [];

  return [...tableData.list].sort((a, b) => {
    if (a.field?.includes("custom")) return -1;
    if (b.field?.includes("custom")) return 1;
    if (a.field === "data") return -1;
    if (b.field === "data") return 1;
    return (a.label || "").localeCompare(b.label || "");
  });
}

function getGroupFields(field: any) {
  const table = getTableGroups();
  if (!table) return [];

  return table
    .find((g) => g.field === field.query.group)
    ?.list?.sort((a, b) => {
      if (a.field?.includes("custom")) return -1;
      if (b.field?.includes("custom")) return 1;
      if (a.field === "data") return -1;
      if (b.field === "data") return 1;
      return (a.label || "").localeCompare(b.label || "");
    });
}

// -------------------------
// FILTERS
// -------------------------

function checkIsImage(data: any) {
  const table = dataMap.value.find(
    (t) => t.table === internalBlock.value.content.table,
  );

  if (!table) return false;

  const group = table.list?.find(
    (g: any) => g.field === data.query.group,
  ) as any;

  if (!group) return false;
  if (group.is_image) return true;

  const field = group.list?.find(
    (f: any) => f.field === data.query.field,
  ) as any;

  if (!field) return false;
  if (field.is_image) return true;

  return false;
}

function getFilterTables() {
  return dataMap.value
    .map((t) => ({ label: t.label, table: t.table }))
    .sort((a, b) => {
      if (a.table?.includes("custom")) return -1;
      if (b.table?.includes("custom")) return 1;
      if (a.table === "data") return -1;
      if (b.table === "data") return 1;
      return (a.label || "").localeCompare(b.label || "");
    });
}

function getFilterGroups() {
  return dataMap.value
    .find((t) => t.table === internalBlock.value.content.table)
    ?.list?.sort((a, b) => {
      if (a.field?.includes("custom")) return -1;
      if (b.field?.includes("custom")) return 1;
      if (a.field === "data") return -1;
      if (b.field === "data") return 1;
      return (a.label || "").localeCompare(b.label || "");
    });
}

function changeFilterGroup() {
  filter.value.field = null;
  filter.value.value = null;
  getFilterValues();
}

function getFilterFields() {
  return dataMap.value
    .find((t) => t.table === internalBlock.value.content.table)
    ?.list?.find((g: any) => g.field === filter.value.table)
    ?.list?.sort((a, b) => {
      if (a.field?.includes("custom")) return -1;
      if (b.field?.includes("custom")) return 1;
      if (a.field === "data") return -1;
      if (b.field === "data") return 1;
      return (a.label || "").localeCompare(b.label || "");
    });
}

function getFilterValues() {
  filter.value.value = null;

  if (!filter.value.table) return;

  const data = get(resultData.value, props.block.content.table, null);
  const list = toArray(data).flatMap((item: any) =>
    toArray(get(item, filter.value.table!, [])),
  );

  const options = list
    ?.map((item: any) => {
      const value = get(item, filter.value.field!, "");
      const label = getTitle(value);
      return { label, value };
    })
    ?.filter((item: any) => item.label)
    ?.reduce((acc: any[], item: any) => {
      if (!acc.find((i: any) => i.value === item.value)) acc.push(item);
      return acc;
    }, [])
    .sort((a, b) => (a.label || "").localeCompare(b.label || ""));

  filterOptions.value = [
    ...options,
    { label: "Personalizado...", value: "custom" },
  ];
}

function onChangeFilter() {
  const data = get(resultData.value, internalBlock.value.content.table, null);
  const filters: FilterType[] = internalBlock.value.content.filters || [];

  const dataList = toArray(data);

  const list = dataList.filter((item: any) => {
    if (!item) return false;
    if (filters.length === 0) return true;

    return filters.every((filterItem) => {
      const value = filterItem.custom ?? filterItem.value;

      if (isEmptyValue(value)) return true;

      if (filterItem.field?.includes("custom")) {
        const direct = toArray(item?.[filterItem.field!]);
        const nested = toArray(
          get(item, `${filterItem.table}.${filterItem.field}`, null),
        );
        const all = [...direct, ...nested];

        if (all.length === 0) return false;

        return all.some(
          (entry: any) =>
            looseEqual(entry?.label, value) ||
            looseEqual(entry?.name, value) ||
            looseEqual(entry?.value, value),
        );
      }

      const raw = get(item, filterItem.table!, null);
      const tableArray = toArray(raw);

      if (tableArray.length === 0) return false;

      return tableArray.some((t: any) =>
        looseEqual(t?.[filterItem.field!], value),
      );
    });
  });

  const payload = {
    ...internalBlock.value.content,
    rows: Array.from(list).map((data: any) => {
      return (internalBlock.value.content.fields ?? []).map((field: any) => {
        if (!list || field.query.group === null || field.query.field === null) {
          return {
            col: field.col,
            field: field.id,
            id: bsonId(),
            value: "",
            visible: true,
            is_image: false,
          };
        }

        const isImage = checkIsImage(field);

        if (field.query.field?.includes("custom")) {
          const direct = toArray(data?.[field.query.field!]);
          const nested = toArray(
            get(data, `${field.query.group}.${field.query.field}`, null),
          );
          const arr = [...direct, ...nested];

          const found = arr.find((f: any) =>
            looseEqual(f?.label, field.query?.custom),
          );

          return {
            col: field.col,
            field: field.id,
            id: bsonId(),
            is_image: isImage,
            visible: true,
            value: resolveCellValue(found?.value, isImage),
          };
        }

        const group = get(data, field.query.group, null);

        if (Array.isArray(group)) {
          return {
            col: field.col,
            field: field.id,
            id: bsonId(),
            is_image: isImage,
            value: group
              .map((g: any) =>
                resolveCellValue(get(g, field.query.field, ""), isImage),
              )
              .filter((v) => v !== "")
              .join(", "),
            visible: true,
          };
        }

        return {
          col: field.col,
          field: field.id,
          id: bsonId(),
          is_image: isImage,
          value: resolveCellValue(get(group, field.query.field, ""), isImage),
          visible: true,
        };
      });
    }),
  };

  onUpdate("content", payload);
}

function isFilterValueCustom() {
  return (
    filter.value?.value?.includes("custom") ||
    filter.value.field?.includes("custom")
  );
}

function filterClear() {
  filter.value = {
    custom: null,
    field: null,
    table: null,
    value: null,
  };

  filterOptions.value = [];
}

function filterAdd() {
  if (!internalBlock.value.content.filters) {
    internalBlock.value.content.filters = [];
  }

  internalBlock.value.content.filters.push(filter.value);

  onChangeFilter();

  filterClear();
}

function filterRemove(index: any) {
  internalBlock.value.content.filters.splice(index, 1);

  onChangeFilter();
}

const debouncedOnChangeFilter = debounce(onChangeFilter, 500);

watch(
  () => props.block,
  (newValue) => {
    if (newValue) {
      initialize(newValue);
    }
  },
  { deep: true },
);

onMounted(() => {
  if (props.block) {
    initialize(props.block);
  }
});
</script>

<template>
  <div class="table-editor">
    <div class="tabs">
      <template v-for="item in tabs" :key="item.key">
        <div
          class="tab"
          :class="{ active: selectedTab === item.key }"
          @click="selectedTab = item.key"
        >
          {{ item.label }}
        </div>
      </template>
    </div>

    <!-- query -->
    <template v-if="selectedTab === 'query'">
      <div class="tab-query">
        <div class="query-group">
          <div class="query-form">
            <label>Nome do Bloco</label>
            <input
              type="text"
              v-model="internalBlock.content.title"
              @input="onInputCol()"
            />
          </div>
        </div>

        <div class="query-title">Fonte de Dados</div>
        <div class="query-filter">
          <div class="filter-form">
            <label>Tabela</label>
            <select
              v-model="internalBlock.content.table"
              @change="
                onChangeTable();
                filterClear();
              "
            >
              <option :value="null">Selecione...</option>
              <template v-for="t in getFilterTables()" :key="t.table">
                <option :value="t.table">{{ t.label }}</option>
              </template>
            </select>
          </div>
        </div>

        <div class="query-title">Filtros</div>
        <div class="query-filter">
          <div class="filter-form">
            <label>Grupo</label>
            <select
              v-model="filter.table"
              :disabled="!internalBlock.content.table"
              @change="changeFilterGroup()"
            >
              <option :value="null">Selecione...</option>
              <template v-for="g in getFilterGroups()" :key="g.field">
                <option :value="g.field">{{ g.label }}</option>
              </template>
            </select>
          </div>

          <div class="filter-form">
            <label>Campo</label>
            <select
              v-model="filter.field"
              :disabled="!filter.table"
              @change="
                getFilterValues();
                filter.value = null;
              "
            >
              <option :value="null">Selecione...</option>
              <template v-for="f in getFilterFields()" :key="f.field">
                <option :value="f.field">{{ f.label }}</option>
              </template>
            </select>
          </div>

          <div class="filter-form">
            <label>Valor</label>
            <select
              v-model="filter.value"
              :disabled="!filter.field || filter.field?.includes('custom')"
            >
              <option :value="null">Selecione...</option>
              <template v-for="f in filterOptions" :key="f.value">
                <option :value="f.value">{{ f.label }}</option>
              </template>
            </select>
          </div>

          <template v-if="isFilterValueCustom()">
            <div class="filter-form">
              <label>&nbsp;</label>
              <input type="text" v-model="filter.custom" />
            </div>
          </template>

          <div class="filter-actions">
            <button
              class="filter-btn"
              :disabled="disableFilterAdd"
              @click="filterAdd()"
            >
              Adicionar
            </button>
            <button
              class="filter-btn"
              :disabled="disableFilterClear"
              @click="filterClear()"
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        <div class="query-title">Filtros Adicionados</div>
        <div class="query-filter">
          <div class="filter-list">
            <template
              v-for="(item, index) in block.content.filters"
              :key="index"
            >
              <div class="filter-item">
                <div class="filter-spans">
                  <span>Campo: {{ getTitle(item.field) }}</span>
                  <template v-if="item.value?.includes('custom')">
                    <span>Valor: {{ item.custom || "—" }}</span>
                  </template>
                  <template v-else>
                    <span>Valor: {{ getTitle(item.value) || "—" }}</span>
                  </template>
                </div>
                <button class="remove-btn" @click="filterRemove(index)">
                  ×
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- cols -->
    <template v-if="selectedTab === 'cols'">
      <div class="tab-cols">
        <draggable
          class="blocks-container"
          v-model="columns"
          item-key="id"
          handle=".drag-handle"
          @end="colReorder"
        >
          <template #item="{ element: col, index: colIndex }">
            <div class="col">
              <div class="col-controls">
                <div class="drag-handle">⠿</div>
                <button
                  class="block-btn"
                  :disabled="colIndex === 0"
                  @click.stop="colMoveUp(colIndex)"
                >
                  ↑
                </button>
                <button
                  class="block-btn"
                  :disabled="colIndex === columns.length - 1"
                  @click.stop="colMoveDown(colIndex)"
                >
                  ↓
                </button>
                <button
                  class="block-btn danger"
                  title="Remover"
                  @click.stop="colRemove(colIndex)"
                >
                  <svg-icon icon="trash" size="1rem" />
                </button>
              </div>

              <div class="col-content">
                <div class="col-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    v-model="col.label"
                    @input="onInputCol()"
                  />
                </div>
              </div>
            </div>
          </template>
        </draggable>

        <div class="actions">
          <button class="btn-add-col" @click="addCol">
            <svg-icon icon="add" size="1rem" />
            Adicionar Coluna
          </button>
        </div>
      </div>
    </template>

    <!-- fields -->
    <template v-if="selectedTab === 'fields'">
      <div class="tab-cols">
        <draggable
          class="blocks-container"
          v-model="fields"
          item-key="id"
          handle=".drag-handle"
          @end="fieldReorder"
        >
          <template #item="{ element: field, index: fieldIndex }">
            <div class="col">
              <div class="col-controls">
                <div class="drag-handle">⠿</div>
                <button
                  class="block-btn"
                  :disabled="fieldIndex === 0"
                  @click.stop="fieldMoveUp(fieldIndex)"
                >
                  ↑
                </button>
                <button
                  class="block-btn"
                  :disabled="fieldIndex === fields.length - 1"
                  @click.stop="fieldMoveDown(fieldIndex)"
                >
                  ↓
                </button>
                <button
                  class="block-btn danger"
                  title="Remover"
                  @click.stop="fieldRemove(fieldIndex)"
                >
                  <svg-icon icon="trash" size="1rem" />
                </button>
              </div>

              <div class="col-content">
                <div class="col-group">
                  <label>Coluna</label>
                  <select
                    v-model="field.col"
                    @change="fieldChangeCol(fieldIndex)"
                  >
                    <option :value="null">Selecione...</option>
                    <template v-for="g in colOptions" :key="g.value">
                      <option :value="g.value">{{ g.label }}</option>
                    </template>
                  </select>
                </div>

                <div class="col-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    v-model="field.label"
                    @input="onInputCol()"
                  />
                </div>
              </div>

              <template v-if="isTableSelected()">
                <div class="col-query">
                  <div class="query-title">Fonte de Dados</div>
                  <div class="query-content">
                    <div class="col-group">
                      <label>Grupo</label>
                      <select
                        v-model="field.query.group"
                        @change="onChangeGroup(field)"
                      >
                        <option :value="null">Selecione...</option>
                        <template v-for="g in getTableGroups()" :key="g.field">
                          <option :value="g.field">{{ g.label }}</option>
                        </template>
                      </select>
                    </div>

                    <template v-if="field.query.group">
                      <div class="col-group">
                        <label>Campo</label>
                        <select
                          v-model="field.query.field"
                          @change="fieldChangeField(fieldIndex)"
                        >
                          <option :value="null">Selecione...</option>
                          <template
                            v-for="f in getGroupFields(field)"
                            :key="f.field"
                          >
                            <option :value="f.field">{{ f.label }}</option>
                          </template>
                        </select>
                      </div>
                    </template>

                    <template v-if="field.query.field?.includes('custom')">
                      <div class="col-group">
                        <label>Nome</label>
                        <input
                          type="text"
                          v-model="field.query.custom"
                          @input="
                            debouncedOnChangeFilter();
                            field.label = field.query.custom;
                          "
                        />
                      </div>
                    </template>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </draggable>

        <div class="actions">
          <button class="btn-add-col" @click="fieldAdd">
            <svg-icon icon="add" size="1rem" />
            Adicionar Campo
          </button>
        </div>
      </div>
    </template>

    <!-- style -->
    <template v-if="selectedTab === 'style'">
      <div class="tab-content">
        <div class="form-group">
          <label>Estilo de Fonte</label>
          <base-select
            v-model="internalStyle['font-family']"
            :value="internalStyle['font-family']"
            height="32px"
            :is-empty="false"
            :options="optionsFonts"
            @change="onUpdate('style', internalStyle)"
          />
        </div>

        <div class="form-group">
          <label>Tamanho da Fonte</label>
          <base-select
            v-model="internalStyle['font-size']"
            :value="internalStyle['font-size']"
            height="32px"
            :is-empty="false"
            :options="optionsFontSize"
            @change="onUpdate('style', internalStyle)"
          />
        </div>

        <div class="form-group">
          <label>Fromatação</label>
          <div class="switchs">
            <base-switch
              :value="internalStyle['font-style'] === 'italic'"
              label="Itálico"
              @change="changeSwitch('font-style', $event, 'italic')"
            />
            <base-switch
              :value="internalStyle['text-decoration'] === 'underline'"
              label="Sublinhado"
              @change="changeSwitch('text-decoration', $event, 'underline')"
            />
            <base-switch
              :value="internalStyle['text-decoration'] === 'line-through'"
              label="Tachado"
              @change="changeSwitch('text-decoration', $event, 'line-through')"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Cor da Fonte</label>
          <div class="color-group">
            <input
              class="color-input"
              type="text"
              v-model="internalStyle['color']"
              maxlength="7"
              @input="normalizeHex"
              @change="onUpdate('style', internalStyle)"
              placeholder="#000000"
            />
            <ColorPicker
              v-model:pureColor="internalStyle['color']"
              format="hex"
              @update:pureColor="onUpdate('style', internalStyle)"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.table-editor {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;

  .tabs {
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid #ccc;

    .tab {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      height: 40px;
      font-size: 13px;
      cursor: pointer;
      user-select: none;

      &.active {
        border-bottom: 2px solid #000;
      }
    }
  }

  .tab-query {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: auto;
    padding-inline: 15px;
    padding-block-start: 15px;
    padding-block-end: 20px;

    .query-title {
      font-size: 0.875rem;
      font-weight: bold;
      margin-bottom: 0.5rem;
    }

    .query-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-bottom: 1rem;
    }

    .query-form {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      align-items: flex-start;
      margin-bottom: 0.5rem;

      label {
        font-size: 0.875rem;
      }

      input {
        font-size: 0.875rem;
        width: calc(100% - 20px);
        height: 34px;
        padding-inline: 10px;
        padding-block: 0px;
        border: 1px solid #ccc;
        border-radius: 4px;
      }

      select {
        font-size: 0.875rem;
        width: 100%;
        height: 34px;
        padding-inline: 10px;
        padding-block: 0px;
        border: 1px solid #ccc;
        border-radius: 4px;
      }
    }

    .query-filter {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-bottom: 1.5rem;

      .filter-form {
        display: grid;
        grid-template-columns: 2fr 4fr;
        gap: 0.25rem;
        align-items: center;

        label {
          font-size: 0.875rem;
        }

        input {
          font-size: 0.875rem;
          width: calc(100% - 20px);
          height: 34px;
          padding-inline: 10px;
          padding-block: 0px;
          border: 1px solid #ccc;
          border-radius: 4px;
        }

        select {
          font-size: 0.875rem;
          width: 100%;
          height: 34px;
          padding-inline: 10px;
          padding-block: 0px;
          border: 1px solid #ccc;
          border-radius: 4px;
        }
      }

      .filter-actions {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        padding-block: 1rem;

        button {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          height: 34px;
          padding-inline: 10px;
          border: 1px solid #ccc;
          border-radius: 4px;
          cursor: pointer;
          background: #fff;
        }
      }

      .filter-list {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        row-gap: 0.5rem;

        .filter-item {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding-block: 8px;
          padding-inline: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          background: #fff;
          gap: 10px;

          .filter-spans {
            display: flex;
            flex-direction: column;
          }

          button {
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 0px;
            width: 18px;
            height: 18px;
            font-size: 18px;
          }
        }
      }
    }
  }

  .tab-cols {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: calc(100% - 20px);
    min-height: 0;
    gap: 1rem;
    overflow: auto;
    padding-block-end: 20px;

    .col {
      display: flex;
      flex-direction: column;
      border-bottom: 1px solid #ccc;
      padding-inline: 15px;
      padding-bottom: 0.5rem;

      .col-content {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding-block-end: 1rem;
      }

      .col-query {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        background: #fff;
        margin-block-end: 1rem;
        padding: 0.5rem;
        border: 1px solid #ccc;

        .query-title {
          font-size: 0.75rem;
          font-weight: bold;
        }
        .query-content {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
      }

      .col-controls {
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
        padding-block: 10px;

        .drag-handle {
          padding: 2px 6px;
          background: #6c757d;
          color: white;
          cursor: grab;
          font-size: 12px;
          border-radius: 4px;
        }

        .block-btn {
          padding: 2px 6px;
          background: #6c757d;
          color: white;
          border: none;
          font-size: 12px;
          cursor: pointer;
          border-radius: 4px;

          &:hover {
            background: #545b62;
          }
          &:disabled {
            background: #ccc;
            cursor: not-allowed;
          }
          &.danger {
            margin-left: auto;
            padding-inline: 2px;
          }
          &.danger:hover {
            background: #dc3545;
          }
        }
      }
    }

    .actions {
      padding-inline: 15px;
    }

    .btn-add-col {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      width: 100%;
      padding-inline: 15px;
      padding-block: 10px;
      border: 1px solid #ccc;
      border-radius: 4px;
      background: #fff;
      cursor: pointer;

      &:hover {
        background: #fafafa;
      }
    }

    .col-group {
      display: grid;
      grid-template-columns: 1fr 4fr;
      gap: 0.5rem;
      align-items: center;

      label {
        font-size: 0.8rem;
      }

      input {
        font-size: 0.75rem;
        width: calc(100% - 20px);
        height: 28px;
        padding-inline: 10px;
        padding-block: 0px;
        border: 1px solid #ccc;
        border-radius: 4px;
      }

      select {
        font-size: 0.75rem;
        width: 100%;
        height: 28px;
        padding-inline: 10px;
        padding-block: 0px;
        border: 1px solid #ccc;
        border-radius: 4px;
      }
    }
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 1rem;
    overflow: auto;
    padding-inline: 15px;
    padding-block-start: 15px;
    padding-block-end: 20px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    label {
      font-size: 0.875rem;
      font-weight: bold;
    }

    .switchs {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .color-group {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 1rem;

      .color-input {
        width: calc(100% - 20px);
        height: 30px;
        padding-inline: 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
      }
    }

    .line-group {
      display: grid;
      grid-template-columns: 1fr 1fr;
      align-items: center;
      gap: 0.5rem;

      .label {
        font-size: 0.875rem;
      }

      input {
        height: 32px;
        width: calc(100% - 22px);
        padding-inline: 10px;
        padding-block: 0px;
        font-size: 1rem;
        border: 1px solid #ccc;
        border-radius: 5px;
      }
    }
  }
}

:deep(.vc-color-wrap) {
  height: 30px;
  margin-right: 0px;
  padding: 1px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #ccc;
  box-shadow: none;
}

:deep(.vc-color-wrap .current-color) {
  border-radius: 4px;
}

:deep(.vc-color-wrap.transparent) {
  background-image: none;
}
</style>
